import { supabase } from '../config/supabaseConfig.ts';
import { agentsService } from './agentsService';

// Empleados de la plataforma (script 47): entran al mismo panel pero solo ven la sección Agentes, con sus agentes y los públicos.
// El usuario de acceso lo crea la función `admin-employee` (con la llave de servicio); el resto va por RPC.
const call = async (body) => {
  const { data, error } = await supabase.functions.invoke('admin-employee', { body });
  if (error) {
    // supabase-js solo dice «non-2xx»: el motivo real viene en el cuerpo de la respuesta de la función.
    const res = error.context;
    let detail = '';
    try { const text = await res?.clone?.().text?.(); detail = (() => { try { return JSON.parse(text).error || ''; } catch { return text || ''; } })(); } catch { /* sin cuerpo */ }
    const status = res?.status;
    if (status === 404 || /not found/i.test(detail) || /Failed to send|FunctionsFetchError/i.test(error.message || '')) throw new Error('La función admin-employee no está publicada en Supabase.');
    if (status === 401 || status === 403) throw new Error(detail || 'Tu sesión no tiene permiso de administrador para crear empleados.');
    throw new Error(detail || error.message || 'No se pudo crear el empleado.');
  }
  if (data?.error) throw new Error(data.error);
  return data;
};

/** Contraseña temporal legible: sin letras que se confunden (0/O, 1/l). */
export const tempPassword = (length = 12) => {
  const alphabet = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
};

export const employeesService = {
  async list() {
    const { data, error } = await supabase.rpc('employee_list');
    if (error) throw error;
    return data || [];
  },

  /**
   * Alta: proyecto interno del empleado → usuario de acceso (y su registro) → motor encendido en ese proyecto.
   * Si falla el usuario, el proyecto interno ya existe: el mensaje lo dice para no duplicarlo.
   */
  async create({ fullName, email, password }) {
    const types = await agentsService.listProjectTypes();
    const type = types.find((t) => /leads/i.test(t.name || '')) || types[0];
    if (!type) throw new Error('No hay tipos de agente para crear el proyecto interno.');
    const project = await agentsService.createProjectQuick({ name: `Interno · ${fullName}`.slice(0, 100), description: 'Proyecto interno de un empleado (agentes en construcción)', type });
    let created;
    try { created = await call({ action: 'create', email, password, full_name: fullName, project_id: project.id }); }
    catch (e) {
      // El usuario no se creó: se quita el proyecto interno para no dejar uno huérfano (si no se puede, se avisa).
      const { error: delError } = await supabase.from('project').delete().eq('id', project.id);
      throw new Error(delError ? `${e.message} (El proyecto «${project.name}» quedó creado: bórralo en Proyectos.)` : e.message);
    }
    try { await agentsService.setEngine(project.id, 'runtime'); } catch { /* el admin puede encenderlo después desde un agente */ }
    return { ...created, project };
  },

  async setActive(authUserId, active) {
    const { error } = await supabase.rpc('employee_set_active', { p_user: authUserId, p_active: active });
    if (error) throw error;
  },
  resetPassword: (authUserId, password) => call({ action: 'password', auth_user_id: authUserId, password }),
  remove: (authUserId) => call({ action: 'delete', auth_user_id: authUserId }),
};
